import { useEffect, useMemo, useState } from 'react';
import { INTEGRATIONS } from '../constants';
import { useUserSelector } from 'js/store/hooks';
import { pick } from 'ramda';
import { useAddSubscription, useGetPlayerTags, usePushSubscription } from 'server';
import { htcmdService } from 'js/services/htcmd.service';

export const useSelectedIntegration = ({
    cafEnabled,
    xpointEnabled,
    xpointNativeEnabled,
    playerTag
}: {
    cafEnabled: boolean;
    xpointEnabled: boolean;
    xpointNativeEnabled: boolean;
    playerTag: string;
}) => {
    const {
        isSessionStarted,
        windowSessionId
    } = useUserSelector(pick(['isSessionStarted', 'windowSessionId', 'isLoggedIn']));
    const addSubscription = useAddSubscription();
    const getPlayerTags = useGetPlayerTags();
    const [hasTag, setHasTag] = useState(false);

    useEffect(() => {
        if (!isSessionStarted || !playerTag) {
            return;
        }

        (async () => {
            await addSubscription({
                windowSessionId: windowSessionId ?? -1,
                subscriptionTags: [playerTag]
            });

            const { tags: [tag] } = await getPlayerTags({ tagNames: [playerTag] });

            setHasTag(tag?.name === playerTag);
        })();

        return () => {
            setHasTag(false);
        };
    }, [
        isSessionStarted,
        windowSessionId,
        playerTag,
        getPlayerTags,
        addSubscription
    ]);

    usePushSubscription(31133, () => (pushMessage) => {
        const { notificationPlayerTagChangeInfo } = pushMessage?.data?.notification || {};

        if (!notificationPlayerTagChangeInfo) {
            return;
        }

        const {
            tag,
            removed
        } = notificationPlayerTagChangeInfo;

        if (!removed && tag === playerTag) {
            setHasTag(true);
        }
    }, [
        playerTag
    ]);

    return useMemo(() => {
        if (playerTag && !hasTag) {
            return INTEGRATIONS.DISABLED;
        }

        if (htcmdService.isWrapperEnabled) {
            if (xpointNativeEnabled) {
                return INTEGRATIONS.XPOINT_NATIVE;
            }

            return INTEGRATIONS.DISABLED;
        }

        if (htcmdService.isEnabled) {
            return INTEGRATIONS.DISABLED;
        }

        if (cafEnabled) {
            return INTEGRATIONS.CAF;
        }

        if (xpointEnabled) {
            return INTEGRATIONS.XPOINT;
        }

        return INTEGRATIONS.DISABLED;
    }, [
        hasTag,
        playerTag,
        cafEnabled,
        xpointEnabled,
        xpointNativeEnabled
    ]);
};
